/**
 * FJ Unified Cart Progress Bar
 * ----------------------------
 * Goals come from two shop metafields, never from per-campaign app config:
 *   custom.sale_tiers      -> [{min, pct}]  (mirrors the live automatic discounts)
 *   custom.cart_milestones -> { free_shipping: {...}, gwp: {...}, display: {...} }
 *
 * TWO BASES, ONE AXIS
 * Discount tiers are evaluated by Shopify on the PRE-discount subtotal.
 * Free shipping is evaluated on the DISCOUNTED total.
 * The axis is pre-discount dollars; the shipping marker is projected onto it using the
 * cart's observed discount ratio, so it stays honest no matter which discounts are live.
 */
(function () {
  if (window.__fjProgressBar) return;
  window.__fjProgressBar = true;

  var MONEY_FORMAT = '${{amount}}';

  function money(dollars) {
    var n = Math.max(0, dollars);
    // Round up to the cent the shopper must actually reach.
    var s = (Math.ceil(n * 100) / 100).toFixed(2);
    if (s.slice(-3) === '.00') s = s.slice(0, -3);
    return MONEY_FORMAT.replace(/\{\{\s*amount[^}]*\}\}/, s);
  }

  function parseJSONAttr(el, name, fallback) {
    var raw = el.getAttribute(name);
    if (!raw) return fallback;
    try {
      var v = JSON.parse(raw);
      return v == null ? fallback : v;
    } catch (e) {
      return fallback;
    }
  }

  /** Highest qualifying sale percent for a given PRE-discount subtotal. */
  function pctFor(preSub, tiers, basePct) {
    var pct = basePct;
    (tiers || []).forEach(function (t) {
      var min = parseFloat(t.min);
      var p = parseFloat(t.pct);
      if (isFinite(min) && isFinite(p) && preSub >= min && p > pct) pct = p;
    });
    return pct;
  }

  /**
   * Build the goal list. Each goal:
   *   { key, axis ($ position on the tier axis), remaining ($ in its own basis), met, label, headline }
   */
  function buildGoals(state) {
    var goals = [];
    var discTotal = state.discTotal;
    var basePct = state.basePct;
    var tiers = state.tiers || [];
    var ms = state.milestones || {};
    var display = ms.display || {};

    // WHICH SUBTOTAL DOES A DISCOUNT TIER MEASURE AGAINST?
    //
    //   'items_subtotal' (DEFAULT) = cart.items_subtotal_price — the subtotal AFTER line-level
    //       discounts (i.e. after the member discount function). This is what Shopify ACTUALLY
    //       evaluates order-discount minimums against today. Verified against a live cart:
    //       $103.98 original - $20.78 member = $83.20, which clears the $50 tier but NOT the
    //       $100 tier, and checkout duly applied 30%, not 40%.
    //
    //   'original' = cart.original_total_price — the true pre-discount subtotal. Use this ONLY
    //       if the discounts are reconfigured so tiers really do measure the original subtotal
    //       (e.g. member discount moved to order-level). Setting it before that is true would
    //       make the bar promise a tier that checkout will not honour — the dangerous direction.
    var tierSub = display.tier_basis === 'original' ? state.origSub : state.preSub;

    // Observed discount ratio — self-calibrating across sale + member + function discounts.
    var ratio = tierSub > 0 ? discTotal / tierSub : 1;
    if (!isFinite(ratio) || ratio <= 0 || ratio > 1) ratio = 1;

    var preSub = tierSub;

    // --- Discount tier goals ---
    if (basePct > 0) {
      tiers.forEach(function (t) {
        var min = parseFloat(t.min);
        var p = parseFloat(t.pct);
        if (!isFinite(min) || !isFinite(p) || min <= 0) return;
        goals.push({
          key: 'tier-' + p,
          isTier: true, // tiers are mutually exclusive — only the highest one ever applies
          axis: min,
          remaining: min - preSub,
          met: preSub >= min,
          label: p + '% off',
          goalText: p + '% off'
        });
      });
    }

    // --- Free shipping ---
    var fs = ms.free_shipping;
    if (fs && fs.enabled !== false && isFinite(parseFloat(fs.threshold))) {
      var showForMember = fs.hide_for_members !== true || !state.isMember;
      if (showForMember) {
        var threshold = parseFloat(fs.threshold);
        var basis = fs.basis === 'pre_discount' ? 'pre_discount' : 'discounted';
        var current = basis === 'pre_discount' ? preSub : discTotal;
        goals.push({
          key: 'free-shipping',
          // Project the discounted threshold back onto the pre-discount axis.
          axis: basis === 'pre_discount' ? threshold : threshold / ratio,
          remaining: threshold - current,
          met: current >= threshold,
          label: fs.label || 'Free shipping',
          goalText: (fs.label || 'Free shipping').toLowerCase()
        });
      }
    }

    // --- GWP tiers (phase 2; renders as soon as milestones.gwp.enabled is true) ---
    var gwp = ms.gwp;
    if (gwp && gwp.enabled === true && Array.isArray(gwp.tiers)) {
      var gBasis = gwp.basis === 'pre_discount' ? 'pre_discount' : 'discounted';
      var gCurrent = gBasis === 'pre_discount' ? preSub : discTotal;
      gwp.tiers.forEach(function (g, i) {
        var min = parseFloat(g.min);
        if (!isFinite(min)) return;
        goals.push({
          key: 'gwp-' + i,
          axis: gBasis === 'pre_discount' ? min : min / ratio,
          remaining: min - gCurrent,
          met: gCurrent >= min,
          label: g.label || 'Free gift',
          goalText: (g.label || 'a free gift').toLowerCase()
        });
      });
    }

    goals.sort(function (a, b) {
      return a.axis - b.axis;
    });
    return goals;
  }

  function render(el, state) {
    var goals = buildGoals(state);
    var headlineEl = el.querySelector('[data-fj-pb-headline]');
    var fillEl = el.querySelector('[data-fj-pb-fill]');
    var ticksEl = el.querySelector('[data-fj-pb-ticks]');
    var earnedEl = el.querySelector('[data-fj-pb-earned]');

    if (!goals.length) {
      el.setAttribute('hidden', '');
      return;
    }
    el.removeAttribute('hidden');

    var axisMax = goals[goals.length - 1].axis || 1;
    var pct = Math.max(0, Math.min(1, state.preSub / axisMax));

    // --- Nearest unmet goal ---
    // Order by distance along the shared PRE-DISCOUNT axis, never by `remaining` —
    // remaining is expressed in each goal's own basis (tiers = pre-discount dollars,
    // free shipping = discounted dollars), so comparing them directly is apples to oranges.
    // `remaining` is still what we DISPLAY, because that's the number the shopper watches
    // tick down in their cart.
    var unmet = goals.filter(function (g) {
      return !g.met;
    });
    unmet.sort(function (a, b) {
      return a.axis - b.axis;
    });
    var next = unmet[0];

    var display = (state.milestones && state.milestones.display) || {};
    var copy = display.copy || {};

    if (next) {
      // "more" is wrong when the cart is empty — you can't add *more* to nothing.
      var isEmpty = state.preSub <= 0;
      var tmpl = isEmpty
        ? copy.empty || 'Spend {amount} to unlock {goal}'
        : copy.progress || 'Add {amount} more to unlock {goal}';

      headlineEl.innerHTML = tmpl
        .replace('{amount}', '<strong>' + money(next.remaining) + '</strong>')
        .replace('{goal}', '<strong>' + next.goalText + '</strong>');
    } else {
      // Every goal met. Name what they actually won — but the discount TIERS ARE MUTUALLY
      // EXCLUSIVE: only the highest one applies. Listing "30% off + 40% off + 50% off"
      // implies they stack, which is false and oversells the offer. Collapse the tiers to
      // the best one, and keep the non-tier rewards (free shipping, GWP) alongside it.
      var bestTier = null;
      var extras = [];
      goals.forEach(function (g) {
        if (g.isTier) {
          if (!bestTier || g.axis > bestTier.axis) bestTier = g;
        } else {
          extras.push(g);
        }
      });

      var won = [];
      if (bestTier) won.push(bestTier.label.toLowerCase());
      extras.forEach(function (g) {
        won.push(g.label.toLowerCase());
      });

      var allTmpl = copy.all_met || display.max_goal_label || "You've unlocked {goal}";
      headlineEl.innerHTML = allTmpl.replace('{goal}', '<strong>' + won.join(' + ') + '</strong>');
    }

    fillEl.style.width = pct * 100 + '%';

    if (display.show_ticks !== false) {
      ticksEl.innerHTML = goals
        .map(function (g) {
          var left = Math.max(0, Math.min(100, (g.axis / axisMax) * 100));
          return (
            '<span class="fj-pb__tick' +
            (g.met ? ' is-met' : '') +
            (next && g.key === next.key ? ' is-next' : '') +
            '" style="left:' +
            left +
            '%"><span class="fj-pb__tick-dot"></span>' +
            '<span class="fj-pb__tick-label">' +
            g.label +
            '</span></span>'
          );
        })
        .join('');

      declutter(el, ticksEl);
    }

    // --- Earned line ---
    var met = goals.filter(function (g) {
      return g.met;
    });
    if (met.length && display.show_earned_line === true) {
      earnedEl.removeAttribute('hidden');
      earnedEl.textContent =
        'Unlocked: ' +
        met
          .map(function (g) {
            return g.label;
          })
          .join(' · ');
    } else {
      // Off by default: met ticks are already styled as unlocked, so this line was
      // pure duplication — and it was landing on top of the tick labels.
      earnedEl.setAttribute('hidden', '');
    }
  }

  /**
   * Tick labels collide: the goals sit at fixed axis positions but the free-shipping
   * marker slides as the discount ratio changes, so overlaps are dynamic and can't be
   * solved in static CSS. Measure the rendered labels and, where two would overlap,
   * drop the second one to a second row. If a label still collides on row 2, hide it —
   * but never hide the next goal or a just-unlocked one, since those carry the message.
   */
  function declutter(host, ticksEl) {
    var ticks = Array.prototype.slice.call(ticksEl.querySelectorAll('.fj-pb__tick'));
    if (ticks.length < 2) return;

    // Reset before measuring.
    ticks.forEach(function (t) {
      t.classList.remove('fj-pb__tick--row2');
      t.classList.remove('fj-pb__tick--muted');
    });

    // Layout is only measurable once the element is actually on screen.
    if (!host.offsetParent && host.offsetWidth === 0) return;

    var GAP = 6;
    var rowEnds = [-Infinity, -Infinity]; // right edge of the last label placed in each row

    ticks.forEach(function (tick) {
      var label = tick.querySelector('.fj-pb__tick-label');
      if (!label) return;
      var rect = label.getBoundingClientRect();
      if (!rect.width) return;

      if (rect.left >= rowEnds[0] + GAP) {
        rowEnds[0] = rect.right;
        return; // stays on row 1
      }

      if (rect.left >= rowEnds[1] + GAP) {
        tick.classList.add('fj-pb__tick--row2');
        rowEnds[1] = rect.right;
        return;
      }

      // Still colliding on both rows — suppress, unless it's load-bearing.
      if (tick.classList.contains('is-next')) {
        tick.classList.add('fj-pb__tick--row2');
        rowEnds[1] = rect.right;
      } else {
        tick.classList.add('fj-pb__tick--muted');
      }
    });
  }

  function readState(el) {
    return {
      basePct: parseFloat(el.getAttribute('data-base-pct')) || 0,
      // Subtotal AFTER line-level discounts (member) — what Shopify measures tiers against.
      preSub: parseFloat(el.getAttribute('data-pre-subtotal')) || 0,
      // True pre-discount subtotal — only used when display.tier_basis === 'original'.
      origSub: parseFloat(el.getAttribute('data-original-subtotal')) || 0,
      discTotal: parseFloat(el.getAttribute('data-discounted-total')) || 0,
      isMember: el.getAttribute('data-is-member') === 'true',
      tiers: parseJSONAttr(el, 'data-tiers', []),
      milestones: parseJSONAttr(el, 'data-milestones', {})
    };
  }

  var FjProgressBar = function () {};

  if (typeof HTMLElement === 'function') {
    FjProgressBar = class extends HTMLElement {
      connectedCallback() {
        var fmt = this.getAttribute('data-money-format');
        if (fmt) MONEY_FORMAT = fmt;

        this.state = readState(this);
        render(this, this.state);

        // The mini-cart is rendered while the drawer is still closed (width 0), so labels
        // cannot be measured yet. Re-run the declutter pass once we're actually visible.
        this._repaint = function () {
          render(this, this.state);
        }.bind(this);

        if (typeof IntersectionObserver === 'function') {
          this._io = new IntersectionObserver(
            function (entries) {
              if (entries.some(function (en) { return en.isIntersecting; })) this._repaint();
            }.bind(this)
          );
          this._io.observe(this);
        }
        window.addEventListener('resize', this._repaint);

        // Inside <cart-drawer> the section re-renders server-side on every cart mutation,
        // so this element is destroyed and rebuilt — no listener needed there.
        // Everywhere else, follow the cart live.
        this._onCart = function (e) {
          var cart = e && e.detail && e.detail.cart;
          if (!cart) return;
          this.state.preSub = (cart.items_subtotal_price || 0) / 100;
          this.state.origSub = (cart.original_total_price || cart.items_subtotal_price || 0) / 100;
          this.state.discTotal = (cart.total_price || 0) / 100;
          render(this, this.state);
        }.bind(this);

        document.addEventListener('fj:cart-updated', this._onCart);
      }

      disconnectedCallback() {
        if (this._onCart) document.removeEventListener('fj:cart-updated', this._onCart);
        if (this._repaint) window.removeEventListener('resize', this._repaint);
        if (this._io) this._io.disconnect();
      }
    };

    if (!customElements.get('fj-progress-bar')) {
      customElements.define('fj-progress-bar', FjProgressBar);
    }
  }
})();
